import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chaveClassificacoesPersonalizadas } from '@/lib/classificacao-personalizada';
import { abrirArquivo, baixarArquivo, request } from '@/lib/http';
import {
  type ClassificarDocumentoRequest,
  type ClassificacaoPersonalizada,
  type DocumentoDetalhe,
  documentoDetalheSchema,
  documentoResumoSchema,
  type FormatoLote,
  type ListarDocumentosQuery,
  paginaNumeradaSchema,
  resumoDocumentosSchema,
} from '@fiscoai/contracts';

/** Filtros da tela (o que a pessoa escolhe); escopo de empresas vem do shell. */
export type FiltrosDocumentos = Partial<
  Pick<
    ListarDocumentosQuery,
    | 'direcao'
    | 'modelo'
    | 'situacao'
    | 'statusClassificacao'
    | 'classificacao'
    | 'emitidaDe'
    | 'emitidaAte'
    | 'q'
  >
>;

function consulta(empresaIds: readonly string[], filtros: FiltrosDocumentos) {
  return { empresaIds, ...filtros };
}

/** Página, itens por página e ordenação escolhidos na tabela. */
export type PaginacaoDocumentos = Pick<
  ListarDocumentosQuery,
  'pagina' | 'porPagina' | 'ordenarPor' | 'ordem'
>;

/** Só a página exibida; trocar de página mantém a anterior na tela até a nova chegar. */
export function useDocumentos(
  empresaIds: readonly string[],
  filtros: FiltrosDocumentos,
  paginacao: PaginacaoDocumentos,
) {
  return useQuery({
    queryKey: ['documentos', 'lista', empresaIds, filtros, paginacao],
    queryFn: ({ signal }) =>
      request('/documentos', {
        schema: paginaNumeradaSchema(documentoResumoSchema),
        query: { ...consulta(empresaIds, filtros), ...paginacao },
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

export function useResumoDocumentos(empresaIds: readonly string[], filtros: FiltrosDocumentos) {
  return useQuery({
    queryKey: ['documentos', 'resumo', empresaIds, filtros],
    queryFn: ({ signal }) =>
      request('/documentos/resumo', {
        schema: resumoDocumentosSchema,
        query: consulta(empresaIds, filtros),
        signal,
      }),
  });
}

export function useDocumento(id: string | null) {
  return useQuery({
    queryKey: ['documentos', 'detalhe', id],
    queryFn: ({ signal }) =>
      request(`/documentos/${id ?? ''}`, { schema: documentoDetalheSchema, signal }),
    enabled: id !== null,
  });
}

export function baixarXml(id: string, chave: string | null) {
  return baixarArquivo(`/documentos/${id}/xml`, `${chave ?? id}.xml`);
}

/** As notas do filtro num ZIP: XMLs (spec 0008), PDFs (DANFE) ou os dois (spec 0010). */
export function baixarLote(
  empresaIds: readonly string[],
  filtros: FiltrosDocumentos,
  formato: FormatoLote,
) {
  return baixarArquivo('/documentos/lote.zip', 'notas.zip', {
    ...consulta(empresaIds, filtros),
    formato,
  });
}

/** DANFE em nova aba: o PDF vem por fetch autenticado (Bearer) e abre como blob. */
export function abrirDanfe(id: string) {
  return abrirArquivo(`/documentos/${id}/danfe.pdf`);
}

/**
 * Classifica a nota inteira e/ou itens. Otimista: a classificação aparece na hora; a resposta do
 * server (com CFOP de entrada, grupo e alertas calculados) substitui a prévia.
 */
export function useClassificar(id: string) {
  const cliente = useQueryClient();
  const chave = ['documentos', 'detalhe', id];
  return useMutation({
    mutationFn: (corpo: ClassificarDocumentoRequest) =>
      request(`/documentos/${id}/classificacao`, {
        method: 'PUT',
        body: corpo,
        schema: documentoDetalheSchema,
      }),
    onMutate: async (corpo) => {
      await cliente.cancelQueries({ queryKey: chave });
      const anterior = cliente.getQueryData<DocumentoDetalhe>(chave);
      if (anterior) {
        const lista = cliente.getQueryData<{ items: ClassificacaoPersonalizada[] }>(
          chaveClassificacoesPersonalizadas(anterior.empresaId),
        );
        cliente.setQueryData(chave, previa(anterior, corpo, lista?.items ?? []));
      }
      return { anterior };
    },
    onError: (_erro, _corpo, contexto) => {
      if (contexto?.anterior) cliente.setQueryData(chave, contexto.anterior);
    },
    onSuccess: (detalhe) => {
      cliente.setQueryData(chave, detalhe);
      void cliente.invalidateQueries({ queryKey: ['documentos'], refetchType: 'none' });
      void cliente.invalidateQueries({ queryKey: ['pendencias'] });
    },
  });
}

type ItemClassificado = DocumentoDetalhe['classificacaoItens'][number];
type Escolha = Pick<ItemClassificado, 'classificacao' | 'personalizada'>;

/** O que o corpo pede para um item (ou nada, se ele não muda). */
function escolhaDoCorpo(
  corpo: ClassificarDocumentoRequest,
  nItem: number,
  nome: (id: string | null | undefined) => ItemClassificado['personalizada'],
): Escolha | null {
  const doItem = corpo.itens?.find((i) => i.nItem === nItem);
  if (doItem)
    return {
      classificacao: doItem.classificacao,
      personalizada: nome(doItem.classificacaoPersonalizadaId),
    };
  if (corpo.todos)
    return { classificacao: corpo.todos, personalizada: nome(corpo.todosPersonalizadaId) };
  return null;
}

/** Prévia otimista: só a classificação (e o nome da personalizada) muda; o resto vem do server. */
export function previa(
  doc: DocumentoDetalhe,
  corpo: ClassificarDocumentoRequest,
  personalizadas: readonly ClassificacaoPersonalizada[] = [],
): DocumentoDetalhe {
  const nome = (id: string | null | undefined) => {
    const p = id ? personalizadas.find((x) => x.id === id) : undefined;
    return p ? { id: p.id, nome: p.nome } : null;
  };
  return {
    ...doc,
    classificacaoItens: doc.classificacaoItens.map((c) => ({
      ...c,
      ...escolhaDoCorpo(corpo, c.nItem, nome),
    })),
  };
}
